/**
 * reachabilitySustained — is the unreachable verdict CONFIRMED? (2026-10-27)
 *
 * Charter: count consecutive failed reachability probes and answer whether the
 * visible OFFLINE banner may show. Fed by `refreshReachability` (the only
 * writer), read by OfflineIndicator.
 *
 * WHY: one failed probe is not enough evidence to paint a red "Offline" strip
 * across the app. Measured symptom (Mission Control, operator): the bar showed
 * WHILE data was visibly loading live. The probe is a same-origin request; when
 * a dashboard has a burst of slow view-proxy reads in flight, the probe can
 * queue behind them (per-host connection limit) and hit its 5 s abort — one
 * timed-out probe on a network that is carrying data. A second, independent
 * failure is the confirmation.
 *
 * THIS DOES NOT CHANGE THE PROBE'S LAW OR THE WRITE PATH (reachability.ts).
 * `getReachability()` / `isConfirmedOffline()` still flip on the FIRST failure,
 * exactly as before — the queue's posture is untouched. Only the banner's
 * display rule (`isSustainedUnreachable`) asks for the confirmation.
 */

/** Consecutive failed probes that make an unreachable verdict "sustained". */
export const SUSTAINED_UNREACHABLE_PROBES = 2

let consecutiveFailures = 0
let lastSustained = false
let listeners: Array<(sustained: boolean) => void> = []

/** Probes failed in a row (0 after any success). */
export function getConsecutiveProbeFailures(): number {
  return consecutiveFailures
}

/**
 * TRUE once {@link SUSTAINED_UNREACHABLE_PROBES} probes IN A ROW have failed.
 * Never true before any probe ran, and cleared by the first answering probe.
 */
export function isSustainedUnreachable(): boolean {
  return consecutiveFailures >= SUSTAINED_UNREACHABLE_PROBES
}

/** True when a first failure still awaits its confirmation probe. */
export function needsConfirmationProbe(): boolean {
  return consecutiveFailures > 0 && consecutiveFailures < SUSTAINED_UNREACHABLE_PROBES
}

/** Subscribe to TRANSITIONS of {@link isSustainedUnreachable}. Returns an unsubscribe fn. */
export function onSustainedUnreachableChange(listener: (sustained: boolean) => void): () => void {
  listeners.push(listener)
  return () => { listeners = listeners.filter(l => l !== listener) }
}

/** Record one probe verdict and notify on a sustained transition. Called by refreshReachability only. */
export function recordProbeVerdict(reachable: boolean): void {
  consecutiveFailures = reachable ? 0 : consecutiveFailures + 1
  const next = isSustainedUnreachable()
  if (next === lastSustained) return
  lastSustained = next
  for (const listener of listeners) listener(next)
}

/** Reset with the monitor (no stale sustained claim survives a stop). */
export function resetSustainedVerdict(): void {
  consecutiveFailures = 0
  lastSustained = false
  listeners = []
}
